import { Box, Button, Link, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import ReactImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { tokens } from '../../global/theme/tokens';
import { MatchingPropertieShow } from '../../components/exo/matching-properties/MatchingPropertieShow';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RenderHTML from '../../components/preview/RenderHTML';

export const SalesObjectPreview = ({ salesObject }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [arrOfElements, setArrOfElements] = useState({
    documents: [],
    images: [],
    properties: [],
    descriptions: [],
  });

  function getArrOfElements(levels) {
    var images = [];
    var documents = [];
    var descriptions = [];
    var properties = [];

    levels.forEach((element) => {
      // images
      element.images.forEach((img) => {
        images.push({
          original: img.temporaryUrl,
          thumbnail: img.thumbnailUrl,
        });
      });

      // documents
      element.documents.forEach((doc) => {
        documents.push(doc);
      });

      // properties
      element.properties.forEach((prop) => {
        properties.push(prop);
      });

      // texts
      descriptions.push(element.description);
    });

    setArrOfElements({ images, documents, descriptions, properties });
  }

  useEffect(() => {
    if (salesObject) getArrOfElements(salesObject.levels);
  }, [salesObject]);

  const { title } = salesObject || {};

  return (
    <Box className="flex flex-col w-full pt-4 overflow-y-auto items-center h-full  pb-10 ">
      <Box
        className="max-w-[100%] mx-auto flex flex-col gap-2 px-1 pb-5"
        sx={{
          width: '1100px',
          '.image-gallery': {
            width: '100%!important',
          },
          '.image-gallery-slide img': {
            width: '100%',
            maxWidth: '100%',
            aspectRatio: '4/2',
            objectFit: 'cover',
            borderRadius: '10px',
          },
          '.image-gallery-thumbnail img': {
            height: '60px',
            aspectRatio: '3/2',
            objectFit: 'cover',
            borderRadius: '4px',
          },
          '.image-gallery-thumbnail': {
            borderRadius: '7px',
            borderWidth: '3px',
            '&.active': {
              borderColor: colors.info,
            },
            ':hover': {
              borderColor: colors.info,
            },
          },
          'button.image-gallery-bullet': {
            boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
            ':hover': {
              bgcolor: colors.info,
            },
          },
          '.image-gallery-fullscreen-button': {
            filter: 'drop-shadow(rgba(0, 0, 0, 0.5) 0px 0px 5px)',
          },
          '.image-gallery-right-nav, .image-gallery-left-nav': {
            filter: 'drop-shadow(rgba(0, 0, 0, 0.3) 0px 0px 5px)',
            svg: {
              width: '20px',
            },
          },
        }}
      >
        <Typography variant="h1" fontWeight={500}>
          {title}
        </Typography>
        {arrOfElements.images.length > 0 && (
          <ReactImageGallery
            items={arrOfElements.images}
            showPlayButton={false}
            showNav={true}
            showBullets={true}
            thumbnailPosition="right"
          />
        )}
      </Box>
      <Box className="flex justify-center w-full gap-2 flex-col md:flex-row max-w-[1100px]">
        <Box
          sx={{ bgcolor: colors.glass }}
          className="px-4 py-3 rounded-lg w-full md:w-2/3 backdrop-blur-md "
        >
          <Typography variant="h4" fontWeight={600} className="pb-2">
            {t('Information')}
          </Typography>
          {arrOfElements.properties.map((param) => (
            <MatchingPropertieShow key={param.id} property={param} />
          ))}
          {arrOfElements.descriptions.map((description, index) => {
            if (!description) return;
            return (
              <Box className="py-1" key={index}>
                <RenderHTML HTML={description} />
              </Box>
            );
          })}
        </Box>
        {arrOfElements.documents.length > 0 && (
          <Box
            sx={{ bgcolor: colors.glass }}
            className={`w-full md:w-1/3 max-w-full px-4 py-3 rounded-lg backdrop-blur-md flex flex-col gap-1 `}
          >
            <Typography variant="h4" fontWeight={600} className="pb-2">
              {t('Files')}
            </Typography>
            {arrOfElements.documents.map((file) => (
              <DocumentPreview key={file.id} file={file} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const DocumentPreview = ({ file }) => {
  const { title, temporaryUrl } = file;
  return (
    <Link href={temporaryUrl} target="_blank">
      <Button
        variant="outlined"
        disableElevation
        className="flex items-center gap-2 w-full"
      >
        {title}
        <DownloadIcon />
      </Button>
    </Link>
  );
};
